import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogo } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loginWithGoogle, loginWithEmail, register, forgotPassword } = useAuth();
  const [tab, setTab] = useState("login");
  const [error, setError] = useState("");

  useEffect(() => {
    const authError = searchParams.get("auth_error");
    if (authError === "google_login_failed") {
      setError("Google sign-in couldn't be completed. Please try again, or log in with email.");
    } else if (authError === "missing_session") {
      setError("Google sign-in session was missing. Please try again.");
    }
    if (authError) {
      searchParams.delete("auth_error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await forgotPassword(forgotEmail);
      setForgotMessage(res.message);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await loginWithEmail(loginForm.email, loginForm.password);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.name);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white flex items-center justify-center px-6 relative overflow-hidden">
      <Helmet>
        <title>Log In | Skilmark</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Link
        to="/blog"
        data-testid="home-nav-blog-link"
        className="absolute top-6 right-6 text-sm font-semibold text-white/60 hover:text-white transition-colors duration-300 z-10"
      >
        Blog
      </Link>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md glass-panel rounded-3xl p-8 sm:p-10"
      >
        <div className="flex items-center justify-center gap-3 mb-1">
          <img src="/logo.jpg" alt="Skilmark" className="w-12 h-12 rounded-lg object-cover" />
          <span className="font-display font-extrabold text-xl tracking-tight">
            SKIL<span className="text-[#7C93B8]">MARK</span>
          </span>
        </div>
        <p className="text-center text-xs text-white/40 uppercase tracking-[0.2em] mb-8">
          Autonomous Marketing. Infinite Scale.
        </p>

        <button
          data-testid={AUTH.googleLoginBtn}
          onClick={loginWithGoogle}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-[#2563EB] text-white font-semibold px-6 py-3 text-sm hover:bg-[#3B82F6] transition-colors duration-300 mb-6"
        >
          <GoogleLogo size={18} weight="bold" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/30 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {showForgotPassword ? (
          <div>
            {forgotMessage ? (
              <div className="text-center space-y-4">
                <p data-testid={AUTH.forgotPasswordSuccessMessage} className="text-sm text-white/70">{forgotMessage}</p>
                <button
                  type="button"
                  data-testid={AUTH.forgotPasswordBackBtn}
                  onClick={() => { setShowForgotPassword(false); setForgotMessage(""); }}
                  className="text-sm text-[#3B82F6] hover:text-[#60A5FA] transition-colors duration-300"
                >
                  Back to log in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-white/50">Enter your email and we'll send you a link to reset your password.</p>
                <div>
                  <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Email</Label>
                  <Input
                    data-testid={AUTH.forgotPasswordEmailInput}
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="bg-white/5 border-white/15 text-white h-11"
                  />
                </div>
                <button
                  data-testid={AUTH.forgotPasswordSubmitBtn}
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#2563EB] text-white font-semibold px-6 py-3 text-sm hover:bg-[#3B82F6] transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
                <button
                  type="button"
                  data-testid={AUTH.forgotPasswordBackBtn}
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-center text-xs text-white/40 hover:text-white/70 transition-colors duration-300"
                >
                  Back to log in
                </button>
              </form>
            )}
          </div>
        ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full bg-white/5 mb-6">
            <TabsTrigger data-testid={AUTH.authTabLogin} value="login">Log In</TabsTrigger>
            <TabsTrigger data-testid={AUTH.authTabRegister} value="register">Create Account</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Email</Label>
                <Input
                  data-testid={AUTH.loginEmailInput}
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                  className="bg-white/5 border-white/15 text-white h-11"
                />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Password</Label>
                <Input
                  data-testid={AUTH.loginPasswordInput}
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                  className="bg-white/5 border-white/15 text-white h-11"
                />
              </div>
              <button
                data-testid={AUTH.loginSubmitBtn}
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#2563EB] text-white font-semibold px-6 py-3 text-sm hover:bg-[#3B82F6] transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Logging in..." : "Log In"}
              </button>
              <button
                type="button"
                data-testid={AUTH.forgotPasswordLink}
                onClick={() => { setError(""); setForgotMessage(""); setShowForgotPassword(true); }}
                className="w-full text-center text-xs text-white/40 hover:text-white/70 transition-colors duration-300"
              >
                Forgot password?
              </button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Name</Label>
                <Input
                  data-testid={AUTH.registerNameInput}
                  required
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, name: e.target.value }))}
                  className="bg-white/5 border-white/15 text-white h-11"
                />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Email</Label>
                <Input
                  data-testid={AUTH.registerEmailInput}
                  type="email"
                  required
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, email: e.target.value }))}
                  className="bg-white/5 border-white/15 text-white h-11"
                />
              </div>
              <div>
                <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Password</Label>
                <Input
                  data-testid={AUTH.registerPasswordInput}
                  type="password"
                  required
                  minLength={6}
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, password: e.target.value }))}
                  className="bg-white/5 border-white/15 text-white h-11"
                />
              </div>
              <button
                data-testid={AUTH.registerSubmitBtn}
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#F97316] text-white font-semibold px-6 py-3 text-sm hover:bg-[#FB923C] transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
        )}

        <AnimatePresence>
          {error && (
            <motion.p
              data-testid={AUTH.authError}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-sm text-red-400 mt-4 text-center"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Login;
